import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n, { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";
import { supabase } from "@/integrations/supabase/client";

// ── Safe localStorage wrappers ──────────────────────────────────
// iOS Safari Private Browsing throws on localStorage writes; ITP can clear
// storage after 7 days; PWA standalone sometimes runs without storage access.
// Without these wrappers a single throw kills the whole locale-init path
// and the user is silently stuck in the default Spanish.
const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* noop */ }
};

// ── Markets ─────────────────────────────────────────────────────
export const SUPPORTED_MARKETS = ["mx", "us", "de"] as const;
export type SupportedMarket = (typeof SUPPORTED_MARKETS)[number];

export const MARKET_LABELS: Record<SupportedMarket, string> = {
  mx: "México",
  us: "United States",
  de: "Deutschland",
};

// Default market-language mapping (can be overridden by user)
export const MARKET_DEFAULT_LANGUAGE: Record<SupportedMarket, SupportedLanguage> = {
  mx: "es",
  us: "en",
  de: "de",
};

// Reverse mapping: the canonical market for each language. Used to keep the
// market in sync whenever the language changes, so content queries (which filter
// by both language AND market) never desync.
export const LANGUAGE_DEFAULT_MARKET: Record<SupportedLanguage, SupportedMarket> = {
  es: "mx",
  en: "us",
  de: "de",
};

// ── Context ─────────────────────────────────────────────────────
interface LocaleContextType {
  /** Current UI language (es | en) */
  language: SupportedLanguage;
  /** Current market/region (mx | us) */
  market: SupportedMarket;
  /** Change language independently */
  setLanguage: (lang: SupportedLanguage) => void;
  /** Change market and sync its default language */
  setMarket: (market: SupportedMarket) => void;
  /** Change both at once */
  setLocale: (lang: SupportedLanguage, market: SupportedMarket) => void;
}

const LocaleContext = createContext<LocaleContextType>({
  language: "en",
  market: "us",
  setLanguage: () => {},
  setMarket: () => {},
  setLocale: () => {},
});

export const useLocale = () => useContext(LocaleContext);

// ── Provider ────────────────────────────────────────────────────
const MARKET_STORAGE_KEY = "app_market";
const LANGUAGE_STORAGE_KEY = "app_language";
const LANGUAGE_OVERRIDE_STORAGE_KEY = "app_language_user_selected";
const DEVICE_LANG_DETECTED_KEY = "lang_set_by_device";

const isSupportedLanguage = (value: string | null | undefined): value is SupportedLanguage => {
  return !!value && SUPPORTED_LANGUAGES.includes(value as SupportedLanguage);
};

const normalizeLanguage = (value: string | null | undefined): SupportedLanguage | null => {
  const normalized = value?.substring(0, 2).toLowerCase();
  return isSupportedLanguage(normalized) ? normalized : null;
};

/**
 * Detect the user's preferred language from every reliable source available.
 * Order of trust:
 *   1. navigator.languages[] (full list of preferences)
 *   2. navigator.language (current top preference)
 *   3. Intl.DateTimeFormat resolved locale — ONLY when navigator gave us
 *      nothing at all (some iOS PWA standalone contexts don't populate
 *      navigator.language). Intl reflects the JS engine/OS locale, which can
 *      disagree with the browser's own language setting (e.g. an OS/ICU
 *      default of "es" on a device whose browser is actually set to
 *      Portuguese) — if navigator DID report a language and it just isn't
 *      one we support (es/en/de), that "unsupported" signal is real and
 *      must not be second-guessed by Intl's guess. Letting Intl override it
 *      here previously caused unrelated-language devices to silently land
 *      on Spanish instead of falling through to the English default.
 */
function detectDeviceLanguage(): SupportedLanguage | null {
  // navigator.languages — array of preferred languages, most preferred first
  const navigatorCandidates: string[] = [];
  if (typeof navigator !== "undefined") {
    if (Array.isArray(navigator.languages)) navigatorCandidates.push(...navigator.languages);
    if (navigator.language) navigatorCandidates.push(navigator.language);
  }

  for (const candidate of navigatorCandidates) {
    const normalized = normalizeLanguage(candidate);
    if (normalized) return normalized;
  }

  if (navigatorCandidates.length === 0) {
    try {
      const intlLocale = Intl.DateTimeFormat().resolvedOptions().locale;
      const normalized = normalizeLanguage(intlLocale);
      if (normalized) return normalized;
    } catch { /* noop */ }
  }

  return null;
}

/**
 * On first launch (no stored language/market), auto-detect from the browser/device locale.
 * English device → set EN + US market. Spanish device → set ES + MX (explicit, so the
 * value gets persisted instead of relying on the implicit default — important on iOS
 * where ITP can clear storage and re-detection then kicks in fresh).
 * Runs only once; user can override via settings afterwards.
 */
function applyDeviceLanguageIfNew(): void {
  if (safeGet(DEVICE_LANG_DETECTED_KEY)) return;
  safeSet(DEVICE_LANG_DETECTED_KEY, "true");

  // Only act if the user has never stored an explicit language choice
  if (safeGet(LANGUAGE_OVERRIDE_STORAGE_KEY) === "true") return;
  if (safeGet(LANGUAGE_STORAGE_KEY)) return;

  const deviceLang = detectDeviceLanguage();
  if (deviceLang === "en") {
    safeSet(LANGUAGE_STORAGE_KEY, "en");
    safeSet(MARKET_STORAGE_KEY, "us");
    // i18n was initialized already with whatever it found in localStorage at init time;
    // if it picked "es" because the storage was empty, force it to honour the freshly
    // detected EN immediately so the very first render is in English.
    if (i18n.language && normalizeLanguage(i18n.language) !== "en") {
      void i18n.changeLanguage("en");
    }
  } else if (deviceLang === "es") {
    safeSet(LANGUAGE_STORAGE_KEY, "es");
    safeSet(MARKET_STORAGE_KEY, "mx");
  } else if (deviceLang === "de") {
    safeSet(LANGUAGE_STORAGE_KEY, "de");
    safeSet(MARKET_STORAGE_KEY, "de");
    if (i18n.language && normalizeLanguage(i18n.language) !== "de") {
      void i18n.changeLanguage("de");
    }
  }
}

// Run immediately (before React renders) so i18n picks it up on init
applyDeviceLanguageIfNew();

export const LocaleProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();

  const [market, setMarketState] = useState<SupportedMarket>(() => {
    const stored = safeGet(MARKET_STORAGE_KEY);
    if (stored && SUPPORTED_MARKETS.includes(stored as SupportedMarket)) {
      return stored as SupportedMarket;
    }
    // No stored market — derive it from whatever language i18n already
    // resolved (device detection runs before this provider mounts), instead
    // of hardcoding "mx". A hardcoded "mx" here could disagree with a
    // correctly-detected "en"/"de" device language whenever the market key
    // specifically never got written (e.g. applyDeviceLanguageIfNew() found
    // i18n had already cached a language and skipped its own writes) — and
    // the [market] effect below would then force-change the language back
    // to Spanish just to match the wrong market. This was the actual cause
    // of screens like Login defaulting to Spanish on undetectable-locale
    // devices even though i18next itself had already resolved "en".
    const detectedLanguage = normalizeLanguage(i18n.language) || "en";
    return LANGUAGE_DEFAULT_MARKET[detectedLanguage];
  });

  // Falls back to "en" (not "es") — matches i18n's own fallbackLng, so an
  // unauthenticated screen like Login never defaults to Spanish just because
  // i18n hasn't finished resolving the device language yet on first render.
  const language = normalizeLanguage(i18n.language) || "en";

  const applyMarket = useCallback((nextMarket: SupportedMarket) => {
    setMarketState(nextMarket);
    safeSet(MARKET_STORAGE_KEY, nextMarket);
  }, []);

  const applyLanguage = useCallback(
    (nextLanguage: SupportedLanguage, manuallySelected: boolean) => {
      // Bug 1 fix: never demote the manual-selection flag back to "false".
      // Once a user has explicitly chosen a language, that choice must persist
      // until they change it again from Settings. Auto-detection paths call
      // this with manuallySelected=false — they must NOT win over an explicit pick.
      const alreadyManual = safeGet(LANGUAGE_OVERRIDE_STORAGE_KEY) === "true";
      if (!manuallySelected && alreadyManual) {
        return;
      }

      safeSet(LANGUAGE_STORAGE_KEY, nextLanguage);
      if (manuallySelected) {
        safeSet(LANGUAGE_OVERRIDE_STORAGE_KEY, "true");
      }
      void i18n.changeLanguage(nextLanguage);

      // Fix 5: persist explicit user choices to Supabase so they sync across devices.
      // Auto-detection changes (manuallySelected = false) are NOT saved — only genuine
      // user-initiated changes from the Settings page count as a preference.
      if (manuallySelected) {
        supabase.auth.getUser().then(({ data }) => {
          if (!data.user) return;
          supabase
            .from("app_users")
            .update({ preferred_language: nextLanguage })
            .eq("auth_id", data.user.id)
            .then(({ error }) => {
              if (error) console.warn("[LocaleContext] Failed to persist language preference:", error);
            });
        });
      }
    },
    [i18n]
  );

  // Keep <html lang="..."> in sync so browsers don't offer to auto-translate
  // content that is already in the correct language.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Reconcile i18n with localStorage on mount.
  // If the stored language was set by `applyDeviceLanguageIfNew()` AFTER i18n was
  // already initialised (race condition that bites iOS PWAs hardest), the stored
  // value will differ from i18n's current language. Force-sync them once.
  useEffect(() => {
    const stored = safeGet(LANGUAGE_STORAGE_KEY);
    if (stored && isSupportedLanguage(stored) && stored !== language) {
      void i18n.changeLanguage(stored);
    }
    // intentionally run once on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync market state from localStorage whenever language changes.
  // useAutoLocale (the single locale resolver) writes the market to
  // localStorage but cannot update React state directly — this effect picks it
  // up, ensuring market is restored after iOS ITP clears localStorage.
  useEffect(() => {
    const storedMarket = safeGet(MARKET_STORAGE_KEY);
    if (storedMarket && SUPPORTED_MARKETS.includes(storedMarket as SupportedMarket) && storedMarket !== market) {
      setMarketState(storedMarket as SupportedMarket);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const setLanguage = useCallback(
    (nextLanguage: SupportedLanguage) => {
      applyLanguage(nextLanguage, true);
      // Keep the market aligned with the chosen language so content queries
      // (filtered by language AND market) never desync. The manual-selection
      // flag set above prevents the [market] effect from fighting this.
      applyMarket(LANGUAGE_DEFAULT_MARKET[nextLanguage]);
    },
    [applyLanguage, applyMarket]
  );

  const setMarket = useCallback(
    (nextMarket: SupportedMarket) => {
      applyMarket(nextMarket);
      applyLanguage(MARKET_DEFAULT_LANGUAGE[nextMarket], false);
    },
    [applyLanguage, applyMarket]
  );

  const setLocale = useCallback(
    (nextLanguage: SupportedLanguage, nextMarket: SupportedMarket) => {
      applyMarket(nextMarket);
      applyLanguage(nextLanguage, nextLanguage !== MARKET_DEFAULT_LANGUAGE[nextMarket]);
    },
    [applyLanguage, applyMarket]
  );

  // When the market changes (e.g. admin switches user to a different region),
  // sync the language to that market's default — but only if the user never
  // explicitly chose a language themselves.
  // Intentionally depends only on [market] so that useAutoLocale setting the
  // language (without the manual-selection flag) does NOT get overridden on
  // every re-render by this effect.
  useEffect(() => {
    const manuallySelected = safeGet(LANGUAGE_OVERRIDE_STORAGE_KEY) === "true";
    if (manuallySelected) return;

    const defaultLanguage = MARKET_DEFAULT_LANGUAGE[market];
    const storedLanguage = safeGet(LANGUAGE_STORAGE_KEY);
    if (storedLanguage && storedLanguage !== defaultLanguage) {
      applyLanguage(defaultLanguage, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);

  return (
    <LocaleContext.Provider value={{ language, market, setLanguage, setMarket, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
};
